import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../../hooks/usePageTitle";
import adminFetch from "../../utils/adminFetch";

function toSlug(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

const EMPTY_SECTIONS = [{ heading: "", paragraphs: [""] }];

const EMPTY_FORM = {
  title: "", date: "", category: "", tags: "", popularity: "80",
  excerpt: "", author: "Paladin Editorial Team",
  role: "Paladin Professional Insurance Solutions",
  image: "",
  sections: EMPTY_SECTIONS,
  published: true,
};

function Spinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24"
        fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label style={{
        fontFamily: "'Cinzel', serif",
        fontSize: "8.5px",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: "rgba(1,46,114,0.4)",
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputBase = {
  fontFamily: "'Constantia', 'Georgia', serif",
  fontSize: "14px",
  color: "#012E72",
  border: "1px solid rgba(1,46,114,0.14)",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  padding: "10px 14px",
  outline: "none",
  width: "100%",
  transition: "border-color 0.15s",
  boxSizing: "border-box",
};

function Input({ value, onChange, placeholder, type = "text", min, max }) {
  return (
    <input
      type={type} min={min} max={max}
      value={value} onChange={onChange} placeholder={placeholder}
      style={inputBase}
      onFocus={e => { e.target.style.borderColor = "#002DB5"; }}
      onBlur={e => { e.target.style.borderColor = "rgba(1,46,114,0.14)"; }}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      rows={rows} value={value} onChange={onChange} placeholder={placeholder}
      style={{ ...inputBase, resize: "vertical" }}
      onFocus={e => { e.target.style.borderColor = "#002DB5"; }}
      onBlur={e => { e.target.style.borderColor = "rgba(1,46,114,0.14)"; }}
    />
  );
}

// ── Section Editor ─────────────────────────────────────────────────────────
function SectionEditor({ sections, onChange }) {
  const update = (fn) => onChange(fn(sections));

  const iconBtn = (label, disabled, onClick) => (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      fontFamily: "monospace", fontSize: "11px",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.25 : 1,
      border: "1px solid rgba(1,46,114,0.14)",
      borderRadius: "5px", padding: "2px 7px",
      backgroundColor: "transparent", color: "#012E72",
      transition: "opacity 0.1s",
    }}>{label}</button>
  );

  const smallBtn = (label, danger, onClick) => (
    <button type="button" onClick={onClick} style={{
      fontFamily: "'Cinzel', serif", fontSize: "7.5px",
      letterSpacing: "0.1em", textTransform: "uppercase",
      cursor: "pointer",
      border: `1px solid ${danger ? "rgba(220,38,38,0.2)" : "rgba(1,46,114,0.15)"}`,
      borderRadius: "5px", padding: "2px 8px",
      backgroundColor: "transparent",
      color: danger ? "#DC2626" : "#012E72",
      transition: "opacity 0.1s",
    }}>{label}</button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {sections.map((sec, i) => (
        <div key={i} style={{
          border: "1px solid rgba(1,46,114,0.12)",
          borderRadius: "10px",
          padding: "14px 16px",
          backgroundColor: "rgba(1,46,114,0.018)",
        }}>
          {/* Section header row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
            <span style={{
              fontFamily: "'Cinzel', serif", fontSize: "8px",
              letterSpacing: "0.18em", textTransform: "uppercase",
              color: "rgba(1,46,114,0.35)",
            }}>
              Section {i + 1}
            </span>
            <div style={{ display: "flex", gap: "5px" }}>
              {iconBtn("↑", i === 0, () => update(s => {
                const a = [...s];
                [a[i - 1], a[i]] = [a[i], a[i - 1]];
                return a;
              }))}
              {iconBtn("↓", i === sections.length - 1, () => update(s => {
                const a = [...s];
                [a[i], a[i + 1]] = [a[i + 1], a[i]];
                return a;
              }))}
              {sections.length > 1 && smallBtn("Remove", true, () =>
                update(s => s.filter((_, idx) => idx !== i))
              )}
            </div>
          </div>

          {/* Heading input */}
          <input
            type="text"
            value={sec.heading}
            onChange={e => update(s => s.map((sec2, idx) =>
              idx === i ? { ...sec2, heading: e.target.value } : sec2
            ))}
            placeholder="Section heading…"
            style={{ ...inputBase, marginBottom: "10px", fontSize: "13px" }}
            onFocus={e => { e.target.style.borderColor = "#002DB5"; }}
            onBlur={e => { e.target.style.borderColor = "rgba(1,46,114,0.14)"; }}
          />

          {/* Paragraphs */}
          {sec.paragraphs.map((para, pi) => (
            <div key={pi} style={{ marginBottom: "8px" }}>
              <textarea
                rows={3}
                value={para}
                onChange={e => update(s => s.map((sec2, idx) =>
                  idx === i
                    ? { ...sec2, paragraphs: sec2.paragraphs.map((p, pidx) => pidx === pi ? e.target.value : p) }
                    : sec2
                ))}
                placeholder={`Paragraph ${pi + 1}…`}
                style={{ ...inputBase, resize: "vertical", fontSize: "13px", marginBottom: "4px" }}
                onFocus={e => { e.target.style.borderColor = "#002DB5"; }}
                onBlur={e => { e.target.style.borderColor = "rgba(1,46,114,0.14)"; }}
              />
              <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                {smallBtn("+ Add Paragraph", false, () => update(s => s.map((sec2, idx) =>
                  idx === i
                    ? { ...sec2, paragraphs: [...sec2.paragraphs.slice(0, pi + 1), "", ...sec2.paragraphs.slice(pi + 1)] }
                    : sec2
                )))}
                {sec.paragraphs.length > 1 && smallBtn("Remove", true, () => update(s => s.map((sec2, idx) =>
                  idx === i
                    ? { ...sec2, paragraphs: sec2.paragraphs.filter((_, pidx) => pidx !== pi) }
                    : sec2
                )))}
              </div>
            </div>
          ))}
        </div>
      ))}

      <button
        type="button"
        onClick={() => update(s => [...s, { heading: "", paragraphs: [""] }])}
        style={{
          fontFamily: "'Cinzel', serif", fontSize: "8px",
          letterSpacing: "0.15em", textTransform: "uppercase",
          cursor: "pointer",
          border: "1px dashed rgba(1,46,114,0.25)",
          borderRadius: "10px", padding: "10px",
          backgroundColor: "transparent", color: "rgba(1,46,114,0.45)",
          width: "100%", transition: "all 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(1,46,114,0.45)"; e.currentTarget.style.color = "#012E72"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(1,46,114,0.25)"; e.currentTarget.style.color = "rgba(1,46,114,0.45)"; }}
      >
        + Add Section
      </button>
    </div>
  );
}

// ── News Panel ──────────────────────────────────────────────────────────────
function NewsPanel() {
  const [articles, setArticles] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [loaded,   setLoaded]   = useState(false);
  const [open,     setOpen]     = useState(false);

  const loadNews = async () => {
    if (loaded) { setOpen(o => !o); return; }
    setOpen(true);
    setLoading(true);
    try {
      const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/news`);
      const json = await res.json();
      setArticles(json.articles || []);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  return (
    <div style={{
      marginTop: "28px",
      backgroundColor: "#fff",
      borderRadius: "16px",
      border: "1px solid rgba(1,46,114,0.09)",
      boxShadow: "0 2px 16px rgba(1,46,114,0.06)",
      overflow: "hidden",
    }}>
      <button
        onClick={loadNews}
        style={{
          width: "100%",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 24px",
          backgroundColor: "rgba(1,46,114,0.025)",
          border: "none", cursor: "pointer",
          borderBottom: open ? "1px solid rgba(1,46,114,0.07)" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "34px", height: "34px", borderRadius: "9px",
            backgroundColor: "rgba(1,46,114,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.55)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
          </div>
          <div style={{ textAlign: "left" }}>
            <p style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "12px", color: "#012E72", letterSpacing: "0.04em" }}>
              Insurance Industry News
            </p>
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "11.5px", color: "rgba(1,46,114,0.4)", fontStyle: "italic" }}>
              Daily feed for content inspiration
            </p>
          </div>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.4)" strokeWidth="2"
          style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div style={{ padding: "20px 24px" }}>
          {loading ? (
            <div style={{ padding: "32px", display: "flex", justifyContent: "center" }}>
              <svg className="animate-spin" width="24" height="24" viewBox="0 0 24 24"
                fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            </div>
          ) : articles.length === 0 ? (
            <p style={{
              fontFamily: "'Constantia', serif", fontSize: "13px",
              color: "rgba(1,46,114,0.45)", fontStyle: "italic",
              textAlign: "center", padding: "24px 0",
            }}>
              No news available. Add a <strong>NEWSAPI_KEY</strong> to your server <strong>.env</strong> to enable the feed.
            </p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "12px" }}>
              {articles.map((a, i) => (
                <a
                  key={i}
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "block", padding: "14px 16px",
                    border: "1px solid rgba(1,46,114,0.08)",
                    borderRadius: "10px", textDecoration: "none",
                    backgroundColor: "rgba(1,46,114,0.015)",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = "rgba(1,46,114,0.22)";
                    e.currentTarget.style.backgroundColor = "rgba(1,46,114,0.04)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = "rgba(1,46,114,0.08)";
                    e.currentTarget.style.backgroundColor = "rgba(1,46,114,0.015)";
                  }}
                >
                  <p style={{
                    fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "11.5px",
                    color: "#012E72", lineHeight: 1.45, marginBottom: "6px",
                    overflow: "hidden", display: "-webkit-box",
                    WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  }}>
                    {a.title}
                  </p>
                  {a.description && (
                    <p style={{
                      fontFamily: "'Constantia', serif", fontSize: "12px",
                      color: "rgba(1,46,114,0.5)", lineHeight: 1.5, marginBottom: "8px",
                      overflow: "hidden", display: "-webkit-box",
                      WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                    }}>
                      {a.description}
                    </p>
                  )}
                  <p style={{
                    fontFamily: "'Cinzel', serif", fontSize: "8px",
                    letterSpacing: "0.1em", textTransform: "uppercase",
                    color: "rgba(1,46,114,0.35)",
                  }}>
                    {a.source}
                    {a.publishedAt ? ` · ${new Date(a.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                  </p>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Post Form Modal ───────────────────────────────────────────────────────────
function PostModal({ post, onClose, onSave }) {
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (post) {
      setForm({
        title:      post.title || "",
        date:       post.date || "",
        category:   post.category || "",
        tags:       (post.tags || []).join(", "),
        popularity: String(post.popularity ?? 80),
        excerpt:    post.excerpt || "",
        author:     post.author || "Paladin Editorial Team",
        role:       post.role || "Paladin Professional Insurance Solutions",
        image:      post.image || "",
        sections:   post.sections && post.sections.length > 0
          ? post.sections.map(s => ({
              heading:    s.heading || "",
              paragraphs: s.paragraphs?.length ? [...s.paragraphs] : [""],
            }))
          : [{ heading: "", paragraphs: [""] }],
        published: post.published !== false,
      });
    } else {
      setForm({ ...EMPTY_FORM, sections: [{ heading: "", paragraphs: [""] }] });
    }
  }, [post]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setFormError(null);
    if (!form.title.trim()) { setFormError("Title is required."); return; }

    const sections = form.sections
      .map(s => ({
        heading:    s.heading.trim(),
        paragraphs: s.paragraphs.map(p => p.trim()).filter(Boolean),
      }))
      .filter(s => s.heading || s.paragraphs.length > 0);

    const payload = {
      title:      form.title.trim(),
      slug:       toSlug(form.title.trim()),
      date:       form.date.trim(),
      category:   form.category.trim(),
      tags:       form.tags.split(",").map(t => t.trim()).filter(Boolean),
      popularity: Number(form.popularity) || 50,
      excerpt:    form.excerpt.trim(),
      author:     form.author.trim(),
      role:       form.role.trim(),
      image:      form.image.trim(),
      sections,
      published:  form.published,
    };
    setSaving(true);
    try {
      const url    = post
        ? `${process.env.REACT_APP_API_URL}/api/admin/blogs/${post._id}`
        : `${process.env.REACT_APP_API_URL}/api/admin/blogs`;
      const method = post ? "PATCH" : "POST";
      const res    = await adminFetch(url, { method, body: JSON.stringify(payload) });
      const json   = await res.json();
      if (!res.ok) throw new Error(json.message || "Save failed.");
      onSave(json.data);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 overflow-y-auto"
      style={{ backgroundColor: "rgba(1,4,20,0.5)", backdropFilter: "blur(4px)", paddingTop: "40px", paddingBottom: "40px" }}
    >
      <div style={{
        width: "100%", maxWidth: "720px",
        backgroundColor: "#F7F4EF",
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 32px 80px rgba(1,46,114,0.22)",
        border: "1px solid rgba(1,46,114,0.1)",
      }}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #012E72, #002DB5)",
          padding: "22px 32px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.28em", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", marginBottom: "4px" }}>
              {post ? "Editing Article" : "New Article"}
            </p>
            <h2 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "16px", color: "#fff", letterSpacing: "0.05em" }}>
              {post ? post.title || "Untitled" : "Create Article"}
            </h2>
          </div>
          <button onClick={onClose} style={{ color: "rgba(255,255,255,0.45)", transition: "color 0.15s", background: "none", border: "none", cursor: "pointer" }}
            onMouseEnter={e => { e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.45)"; }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: "20px", maxHeight: "70vh", overflowY: "auto" }}>

          <Field label="Title">
            <Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Article title…" />
            {form.title && (
              <p style={{ fontFamily: "monospace", fontSize: "10px", color: "rgba(1,46,114,0.35)", marginTop: "2px" }}>
                /{toSlug(form.title)}
              </p>
            )}
          </Field>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <Field label="Date">
              <Input value={form.date} onChange={e => set("date", e.target.value)} placeholder="e.g. Apr 19, 2026" />
            </Field>
            <Field label="Category">
              <Input value={form.category} onChange={e => set("category", e.target.value)} placeholder="Home Insurance" />
            </Field>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <Field label="Tags (comma-separated)">
              <Input value={form.tags} onChange={e => set("tags", e.target.value)} placeholder="Home, Property, Coverage" />
            </Field>
            <Field label="Popularity (0–100)">
              <Input type="number" min="0" max="100" value={form.popularity} onChange={e => set("popularity", e.target.value)} />
            </Field>
          </div>

          <Field label="Excerpt">
            <Textarea rows={2} value={form.excerpt} onChange={e => set("excerpt", e.target.value)} placeholder="Short description shown on listing…" />
          </Field>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <Field label="Author">
              <Input value={form.author} onChange={e => set("author", e.target.value)} />
            </Field>
            <Field label="Author Role">
              <Input value={form.role} onChange={e => set("role", e.target.value)} />
            </Field>
          </div>

          <Field label="Image URL">
            <Input value={form.image} onChange={e => set("image", e.target.value)} placeholder="https://images.unsplash.com/…" />
          </Field>

          <Field label="Article Sections">
            <SectionEditor
              sections={form.sections}
              onChange={sections => set("sections", sections)}
            />
          </Field>

          {formError && (
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "#dc2626" }}>{formError}</p>
          )}

          {/* Published toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              type="button"
              onClick={() => set("published", !form.published)}
              style={{
                position: "relative", width: "40px", height: "22px",
                borderRadius: "999px", border: "none", cursor: "pointer",
                backgroundColor: form.published ? "#002DB5" : "#D1D5DB",
                transition: "background-color 0.2s", flexShrink: 0,
              }}>
              <span style={{
                position: "absolute", top: "3px",
                left: form.published ? "calc(100% - 19px)" : "3px",
                width: "16px", height: "16px", borderRadius: "50%",
                backgroundColor: "#fff", transition: "left 0.2s",
              }} />
            </button>
            <span style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72" }}>
              {form.published ? "Published" : "Draft — hidden from public"}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "18px 32px",
          borderTop: "1px solid rgba(1,46,114,0.08)",
          display: "flex", justifyContent: "flex-end", gap: "10px",
          backgroundColor: "rgba(1,46,114,0.02)",
        }}>
          <button onClick={onClose} style={{
            fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em",
            textTransform: "uppercase", color: "#012E72",
            border: "1px solid rgba(1,46,114,0.2)", borderRadius: "999px",
            padding: "9px 20px", cursor: "pointer", backgroundColor: "transparent",
            transition: "all 0.15s",
          }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = "rgba(1,46,114,0.06)"; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !form.title.trim()} style={{
            fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em",
            textTransform: "uppercase", color: "#fff",
            background: "linear-gradient(135deg, #012E72, #002DB5)",
            border: "none", borderRadius: "999px",
            padding: "9px 24px", cursor: "pointer",
            opacity: (saving || !form.title.trim()) ? 0.4 : 1,
            boxShadow: "0 4px 16px rgba(0,45,181,0.28)",
            transition: "opacity 0.15s",
          }}>
            {saving ? "Saving…" : post ? "Save Changes" : "Publish Article"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminBlog() {
  usePageTitle("Blog Management");
  const navigate = useNavigate();
  const [posts,    setPosts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [modal,    setModal]    = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/blogs`);
        if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
        if (!res.ok) throw new Error("Failed to load blog posts.");
        const json = await res.json();
        setPosts(json.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [navigate]);

  const handleSave = (saved) => {
    setPosts(prev => {
      const idx = prev.findIndex(p => p._id === saved._id);
      return idx >= 0 ? prev.map(p => p._id === saved._id ? saved : p) : [saved, ...prev];
    });
    setModal(null);
  };

  const handleTogglePublished = async (post) => {
    try {
      const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/blogs/${post._id}`, {
        method: "PATCH",
        body: JSON.stringify({ published: !post.published }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message);
      setPosts(prev => prev.map(p => p._id === post._id ? json.data : p));
    } catch (err) { alert(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    setDeleting(id);
    try {
      const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/blogs/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed.");
      setPosts(prev => prev.filter(p => p._id !== id));
    } catch (err) { alert(err.message); }
    finally { setDeleting(null); }
  };

  const published = posts.filter(p => p.published).length;
  const drafts    = posts.length - published;

  return (
    <>
      {/* ── Page Header ── */}
      <div style={{ marginBottom: "28px", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "16px" }}>
        <div>
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.26em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", marginBottom: "6px" }}>
            Admin / Blog
          </p>
          <h1 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "24px", letterSpacing: "0.04em", color: "#012E72", lineHeight: 1 }}>
            Blog Management
          </h1>
        </div>
        <button
          onClick={() => setModal("new")}
          style={{
            fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em",
            textTransform: "uppercase", color: "#fff",
            background: "linear-gradient(135deg, #012E72, #002DB5)",
            border: "none", borderRadius: "999px",
            padding: "11px 24px", cursor: "pointer",
            boxShadow: "0 4px 20px rgba(0,45,181,0.28)",
            whiteSpace: "nowrap", flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
        >
          + New Article
        </button>
      </div>

      {/* ── Stat Pills ── */}
      {!loading && !error && (
        <div style={{ display: "flex", gap: "10px", marginBottom: "22px", flexWrap: "wrap" }}>
          {[
            { label: "Total Articles", value: posts.length, color: "#012E72", bg: "rgba(1,46,114,0.07)" },
            { label: "Published",      value: published,    color: "#065F46", bg: "#D1FAE5" },
            { label: "Drafts",         value: drafts,       color: "#92400E", bg: "#FEF3C7" },
          ].map(s => (
            <div key={s.label} style={{
              backgroundColor: s.bg, borderRadius: "10px",
              padding: "10px 18px", display: "flex", alignItems: "center", gap: "10px",
            }}>
              <span style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px", color: s.color }}>{s.value}</span>
              <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.16em", textTransform: "uppercase", color: s.color, opacity: 0.7 }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {loading && <Spinner />}
      {error   && <p style={{ fontFamily: "'Constantia', serif", color: "#dc2626", fontSize: "14px" }}>{error}</p>}

      {/* ── Articles Table ── */}
      {!loading && !error && (
        <div style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid rgba(1,46,114,0.09)",
          boxShadow: "0 2px 16px rgba(1,46,114,0.06)",
        }}>
          {posts.length === 0 ? (
            <div style={{ padding: "64px 32px", textAlign: "center" }}>
              <div style={{
                width: "52px", height: "52px", borderRadius: "14px",
                backgroundColor: "rgba(1,46,114,0.06)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 16px",
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="1.5">
                  <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
              </div>
              <p style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: "#012E72", marginBottom: "6px" }}>No articles yet</p>
              <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)", fontStyle: "italic" }}>
                Click "+ New Article" to get started.
              </p>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(1,46,114,0.07)" }}>
                  {["Title & Slug", "Category", "Date", "Status", "Actions"].map(h => (
                    <th key={h} style={{
                      padding: "14px 20px", textAlign: "left",
                      fontFamily: "'Cinzel', serif", fontSize: "8px",
                      letterSpacing: "0.18em", textTransform: "uppercase",
                      color: "rgba(1,46,114,0.4)",
                      backgroundColor: "rgba(1,46,114,0.025)",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {posts.map((post, i) => (
                  <tr key={post._id}
                    style={{ borderBottom: i < posts.length - 1 ? "1px solid rgba(1,46,114,0.055)" : "none", transition: "background-color 0.12s" }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = "rgba(1,46,114,0.025)"; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; }}
                  >
                    <td style={{ padding: "16px 20px", maxWidth: "280px" }}>
                      <p style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "13px", color: "#012E72", marginBottom: "3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {post.title}
                      </p>
                      <p style={{ fontFamily: "monospace", fontSize: "10px", color: "rgba(1,46,114,0.32)" }}>
                        /{post.slug}
                      </p>
                    </td>
                    <td style={{ padding: "16px 20px" }}>
                      <span style={{
                        fontFamily: "'Cinzel', serif", fontSize: "8px",
                        letterSpacing: "0.1em", textTransform: "uppercase",
                        color: "#012E72", backgroundColor: "rgba(0,45,181,0.08)",
                        borderRadius: "6px", padding: "4px 10px", whiteSpace: "nowrap",
                      }}>
                        {post.category || "—"}
                      </span>
                    </td>
                    <td style={{ padding: "16px 20px", fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.55)", whiteSpace: "nowrap" }}>
                      {post.date || "—"}
                    </td>
                    <td style={{ padding: "16px 20px" }}>
                      <button
                        onClick={() => handleTogglePublished(post)}
                        style={{
                          fontFamily: "'Cinzel', serif", fontSize: "8px",
                          letterSpacing: "0.1em", textTransform: "uppercase",
                          color:           post.published ? "#065F46" : "#92400E",
                          backgroundColor: post.published ? "#D1FAE5"  : "#FEF3C7",
                          border: "none", borderRadius: "6px",
                          padding: "5px 12px", cursor: "pointer", transition: "opacity 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "0.75"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                      >
                        {post.published ? "● Published" : "○ Draft"}
                      </button>
                    </td>
                    <td style={{ padding: "16px 20px" }}>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button onClick={() => setModal(post)} style={{
                          fontFamily: "'Cinzel', serif", fontSize: "8px",
                          letterSpacing: "0.12em", textTransform: "uppercase",
                          color: "#012E72", border: "1px solid rgba(1,46,114,0.2)",
                          borderRadius: "999px", padding: "6px 14px",
                          cursor: "pointer", backgroundColor: "transparent", transition: "all 0.15s",
                        }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#012E72"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#012E72"; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#012E72"; e.currentTarget.style.borderColor = "rgba(1,46,114,0.2)"; }}>
                          Edit
                        </button>
                        <button onClick={() => handleDelete(post._id)} disabled={deleting === post._id} style={{
                          fontFamily: "'Cinzel', serif", fontSize: "8px",
                          letterSpacing: "0.12em", textTransform: "uppercase",
                          color: "#DC2626", border: "1px solid rgba(220,38,38,0.22)",
                          borderRadius: "999px", padding: "6px 14px",
                          cursor: deleting === post._id ? "not-allowed" : "pointer",
                          backgroundColor: "transparent",
                          opacity: deleting === post._id ? 0.4 : 1, transition: "all 0.15s",
                        }}
                          onMouseEnter={e => { if (deleting !== post._id) { e.currentTarget.style.backgroundColor = "#DC2626"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#DC2626"; } }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.borderColor = "rgba(220,38,38,0.22)"; }}>
                          {deleting === post._id ? "…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Industry News Panel ── */}
      {!loading && !error && <NewsPanel />}

      {/* Modal */}
      {modal !== null && (
        <PostModal
          post={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </>
  );
}
